// src/lib/managers/agenda/freeSlots.ts

import { parseGraphDateTimeToIsoString } from "../../utils/date/format-time";
import { AgendaItem, AgendaTimeSlot } from "./types/agenda";

/** Un créneau n'est proposé qu'entre 7 h et 19 h, heure locale de l'appareil. */
const WORK_DAY_START_HOUR = 7;
const WORK_DAY_END_HOUR = 19;

/**
 * Durée d'un créneau quand l'outil `findFreeTimeSlots` n'en donne pas (champ facultatif) : 60 min,
 * la valeur par défaut annoncée par l'outil de l'Api-Ai.
 */
export const DEFAULT_SLOT_MINUTES = 60;

const MINUTE_MS = 60 * 1000;

/** Intervalle en millisecondes depuis l'époque, fin exclue. */
type Interval = { start: number; end: number };

/**
 * Créneaux libres de `minutes` minutes entre `start` et `end` (F-1.10, F-1.12).
 *
 * Fonction pure : même entrée, même sortie, sans appel réseau ; l'heure courante arrive par `now`.
 *  - les jours sont des jours calendaires, dès minuit local (fuseau de l'appareil) ;
 *  - chaque jour est borné à `[max(7 h, début, maintenant), min(19 h, fin)]`, le début arrondi à la
 *    minute pleine suivante (jamais un créneau déjà entamé) ;
 *  - seuls les événements vraiment occupés comptent (`isBusyEvent`) ; une journée entière occupée
 *    ferme toute la journée ;
 *  - chaque plage libre est découpée en créneaux consécutifs de `minutes` minutes.
 *
 * @param events  - événements de l'agenda couvrant la plage
 * @param start   - début de la plage (ISO 8601)
 * @param end     - fin de la plage (ISO 8601)
 * @param now     - heure courante : rien n'est proposé avant elle
 * @param minutes - durée de chaque créneau, en minutes
 * @returns les créneaux, dans l'ordre chronologique
 * @throws {Error} date illisible, début qui n'est pas avant la fin, ou durée de moins d'une minute
 */
export function computeFreeSlots(
    events: AgendaItem[],
    start: string | Date,
    end: string | Date,
    now: Date,
    minutes: number,
): AgendaTimeSlot[] {
    const range = parseFreeSlotsRange(start, end, minutes);
    if (Number.isNaN(now.getTime())) {
        throw new Error("Invalid current date");
    }

    const slotMs = minutes * MINUTE_MS;
    const earliest = ceilToMinute(
        Math.max(range.start.getTime(), now.getTime()),
    );
    const latest = range.end.getTime();

    const busyEvents = events.filter(isBusyEvent);
    const allDayBlocks = busyEvents
        .filter((event) => event.isAllDay)
        .map(toAllDayBlock)
        .filter((block): block is Interval => block !== null);
    const busyIntervals = mergeIntervals(
        busyEvents
            .filter((event) => !event.isAllDay)
            .map(toInterval)
            .filter((interval): interval is Interval => interval !== null),
    );

    const slots: AgendaTimeSlot[] = [];
    for (
        let day = startOfLocalDay(range.start);
        day.getTime() < latest;
        day = nextLocalDay(day)
    ) {
        const dayStart = day.getTime();
        if (
            allDayBlocks.some(
                (block) => block.start <= dayStart && dayStart < block.end,
            )
        ) {
            continue;
        }

        const window: Interval = {
            start: Math.max(atLocalHour(day, WORK_DAY_START_HOUR), earliest),
            end: Math.min(atLocalHour(day, WORK_DAY_END_HOUR), latest),
        };
        for (const free of freeIntervals(window, busyIntervals, slotMs)) {
            slots.push(...splitIntoSlots(free, slotMs));
        }
    }

    return slots;
}

/**
 * Plage de recherche lue et vérifiée : dates lisibles, début avant la fin, au moins une minute.
 * L'AgendaManager l'appelle avant de lire l'agenda : une date illisible ne coûte aucun appel.
 *
 * @throws {Error} avec un message explicite, sinon
 */
export function parseFreeSlotsRange(
    start: string | Date,
    end: string | Date,
    minutes: number,
): { start: Date; end: Date } {
    if (!Number.isFinite(minutes) || minutes < 1) {
        throw new Error("minutesToAllocate must be at least 1 minute");
    }

    const startDate = toValidDate(start, "start");
    const endDate = toValidDate(end, "end");
    if (startDate >= endDate) {
        throw new Error("Start date must be before end date");
    }

    return { start: startDate, end: endDate };
}

/**
 * Un événement occupe-t-il vraiment son créneau ? (F-1.12)
 *
 * Non s'il est annulé, refusé par l'utilisateur, ou marqué libre. Une journée entière ne bloque que
 * marquée occupée ou « absent » : « Télétravail », « provisoire » ou « travail ailleurs » laissent la
 * journée ouverte.
 */
export function isBusyEvent(event: AgendaItem): boolean {
    if (event.isCancelled) return false;
    if (event.responseStatus?.response === "declined") return false;
    if (event.showAs === "free") return false;
    if (event.isAllDay) {
        return event.showAs === "busy" || event.showAs === "oof";
    }
    return true;
}

/**
 * Jours d'une journée entière en dates du calendrier « AAAA-MM-JJ », fin exclue, lus comme pour les
 * créneaux libres (Graph et Google, sans fin : un seul jour). Pour l'étape `lireAgenda` du contrat
 * par étapes. Jour de début illisible : `null`.
 */
export function allDayCalendarDates(
    event: AgendaItem,
): { start: string; end: string } | null {
    const block = toAllDayBlock(event);
    return block
        ? {
              start: formatCalendarDay(block.start),
              end: formatCalendarDay(block.end),
          }
        : null;
}

// #region Private helpers

/** « AAAA-MM-JJ » du jour local d'un minuit local. */
const formatCalendarDay = (ms: number): string => {
    const day = new Date(ms);
    const pad = (value: number) => String(value).padStart(2, "0");
    return `${day.getFullYear()}-${pad(day.getMonth() + 1)}-${pad(day.getDate())}`;
};

const toValidDate = (value: string | Date, label: "start" | "end"): Date => {
    const date =
        typeof value === "string" && value.trim() !== ""
            ? new Date(value)
            : value instanceof Date
              ? new Date(value.getTime())
              : new Date(NaN);
    if (Number.isNaN(date.getTime())) {
        throw new Error(
            `Invalid ${label} date: ${JSON.stringify(String(value))} (ISO 8601 expected)`,
        );
    }
    return date;
};

const ceilToMinute = (ms: number): number =>
    Math.ceil(ms / MINUTE_MS) * MINUTE_MS;

const startOfLocalDay = (date: Date): Date =>
    new Date(date.getFullYear(), date.getMonth(), date.getDate());

/** Minuit local du lendemain : un jour calendaire, 23 ou 25 h aux changements d'heure. */
const nextLocalDay = (day: Date): Date =>
    new Date(day.getFullYear(), day.getMonth(), day.getDate() + 1);

const atLocalHour = (day: Date, hour: number): number =>
    new Date(day.getFullYear(), day.getMonth(), day.getDate(), hour).getTime();

/**
 * Jour du calendrier d'une journée entière (« AAAA-MM-JJ… »), à minuit local. Graph et Google la
 * rendent à minuit sans fuseau : c'est une date, pas un instant à convertir.
 */
const calendarDay = (dateTime: string | undefined): Date | null => {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateTime ?? "");
    return match
        ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
        : null;
};

/** Jours couverts par une journée entière, de minuit local à minuit local (dernier jour exclu). */
const toAllDayBlock = (event: AgendaItem): Interval | null => {
    const first = calendarDay(event.start?.dateTime);
    if (!first) return null;
    const last = calendarDay(event.end?.dateTime);
    return {
        start: first.getTime(),
        end:
            last && last > first
                ? last.getTime()
                : nextLocalDay(first).getTime(),
    };
};

const toInterval = (event: AgendaItem): Interval | null => {
    const start = parseGraphDateTimeToIsoString(event.start);
    const end = parseGraphDateTimeToIsoString(event.end);
    if (!start || !end) return null;

    const interval = { start: Date.parse(start), end: Date.parse(end) };
    return interval.start < interval.end ? interval : null;
};

/** Intervalles triés, ceux qui se chevauchent ou se touchent fusionnés. */
const mergeIntervals = (intervals: Interval[]): Interval[] => {
    const sorted = [...intervals].sort((a, b) => a.start - b.start);
    const merged: Interval[] = [];
    for (const interval of sorted) {
        const last = merged[merged.length - 1];
        if (last && interval.start <= last.end) {
            last.end = Math.max(last.end, interval.end);
        } else {
            merged.push({ ...interval });
        }
    }
    return merged;
};

/** Plages libres d'au moins `minMs` dans `window`, hors des intervalles occupés (triés, fusionnés). */
const freeIntervals = (
    window: Interval,
    busy: Interval[],
    minMs: number,
): Interval[] => {
    const free: Interval[] = [];
    let cursor = window.start;
    for (const interval of busy) {
        if (interval.end <= cursor) continue;
        if (interval.start >= window.end) break;
        if (interval.start - cursor >= minMs) {
            free.push({ start: cursor, end: interval.start });
        }
        cursor = interval.end;
    }
    if (window.end - cursor >= minMs) {
        free.push({ start: cursor, end: window.end });
    }
    return free;
};

/** Découpe une plage libre en créneaux consécutifs de `slotMs` ; le reste trop court est perdu. */
const splitIntoSlots = (free: Interval, slotMs: number): AgendaTimeSlot[] => {
    const slots: AgendaTimeSlot[] = [];
    for (let start = free.start; start + slotMs <= free.end; start += slotMs) {
        slots.push({
            startDate: new Date(start).toISOString(),
            endDate: new Date(start + slotMs).toISOString(),
        });
    }
    return slots;
};

// #endregion Private helpers
